import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader, Sparkles, X } from 'lucide-react';
import {
  generateArtifactImage, saveArtifactImage, refineArtifactImage, listBrandAssets,
  captionImageVariant,
} from './api';
import RotatingStatusIndicator from '../../../common/RotatingStatusIndicator';
import { IMAGE_GEN_STATEMENTS } from '../../../common/statusStatements';

// The Creative Studio image pipeline accepts at most 14 reference images. Rails
// enforces the same ceiling; this is only so we can warn before sending.
const MAX_REFERENCES = 14;

function fileToRef(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({
      content: String(r.result).split(',')[1],
      content_type: file.type || 'image/png',
      name: file.name,
    });
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * Cursor-anchored image generation for a production artifact.
 *
 * Three modes, decided by props:
 *   target = null, placement 'inline'  -> generate and insert at the cursor
 *   target = {imageId, ...}            -> refine that image and replace in place
 *   placement = 'cover'                -> document-level cover, no editor node
 *   placement = 'slide'                -> an A/V script slide. Stored as 'inline' (the
 *                                         renderer places it in the VIDEO cell), but
 *                                         there is no TipTap document to insert into, so
 *                                         the caller anchors it to the slide instead.
 */
export default function ImageComposer({
  artifactId, editor, target, placement = 'inline', onClose, onInserted,
}) {
  const refining = Boolean(target?.imageId);
  const isCover = placement === 'cover';
  const isSlide = placement === 'slide';
  // Cover and standalone both live outside the text, so neither creates an
  // editor node. Treating only 'cover' that way made a standalone save claim it
  // had been "inserted" into a document it never touched.
  // A slide image has no editor node to insert: the document references it by id from
  // the slide's image_ids, and the caller wires that up after the save.
  const inDocument = !isCover && !isSlide && placement !== 'standalone';

  const [prompt, setPrompt] = useState('');
  const [caption, setCaption] = useState('');
  const [altText, setAltText] = useState('');
  const [variantCount, setVariantCount] = useState(2);
  const [refs, setRefs] = useState([]);
  const [variants, setVariants] = useState([]);
  // Per-generation overrides on top of each asset's default_on. Detaching a
  // logo here never changes the standing brand set.
  const [overrides, setOverrides] = useState({});

  const { data: assetData } = useQuery({
    queryKey: ['training-brand-assets'],
    queryFn: listBrandAssets,
  });
  const assets = assetData?.brand_assets || [];
  const isAttached = (a) => (a.id in overrides ? overrides[a.id] : a.default_on);
  const attachedIds = assets.filter(isAttached).map((a) => a.id);
  const toggle = (a) => setOverrides((o) => ({ ...o, [a.id]: !isAttached(a) }));

  const referenceCount = assets.filter((a) => isAttached(a) && a.usage_mode === 'reference').length;
  const overCap = refs.length + referenceCount > MAX_REFERENCES;

  const gen = useMutation({
    mutationFn: () => (refining
      ? refineArtifactImage(target.imageId, {
        instruction: prompt, reference_images: refs, variant_count: variantCount,
        // Send the current chip state so a refinement re-applies the same brand
        // assets, including re-stamping an overlay logo pixel-exact.
        brand_asset_ids: attachedIds,
      })
      : generateArtifactImage(artifactId, {
        prompt,
        reference_images: refs,
        brand_asset_ids: attachedIds,
        variant_count: variantCount,
      })),
    onMutate: () => setVariants([]),
    onSuccess: (res) => {
      setVariants(res?.variants || []);
      if (res?.references_dropped > 0) {
        toast(`${res.references_dropped} reference(s) were not used: the limit is ${MAX_REFERENCES}.`,
          { icon: '!', duration: 8000 });
      }
    },
    onError: (e) => toast.error(e?.message || 'Generation failed'),
  });

  // A suggestion, not a save: it fills the two fields and the author edits or replaces
  // whatever comes back. Nothing is written until they save the image.
  const suggestCaption = useMutation({
    mutationFn: (variant) => captionImageVariant(artifactId, {
      image_base64: variant.image_base64,
      // What the picture was ASKED for disambiguates a chart the model can see but
      // cannot label.
      prompt: prompt || target?.prompt,
    }),
    onSuccess: (res) => {
      // A non-2xx lands in onError, so anything here is a real answer. It can still be
      // empty when the model had nothing to say about the picture.
      if (!res?.caption && !res?.alt_text) {
        toast.error('JPRO could not describe that image. Type a caption instead.');
        return;
      }
      if (res.caption) setCaption(res.caption);
      if (res.alt_text) setAltText(res.alt_text);
      toast.success('Suggested. Edit it if you want.');
    },
    onError: (e) => toast.error(e?.message || 'Could not suggest a caption'),
  });

  const save = useMutation({
    // Takes the whole variant, not just its bytes, so the variant's own
    // thought_signature is persisted. That signature is what gives the next
    // refinement the model's reasoning context; without it every turn started
    // from a blank slate.
    mutationFn: (variant) => saveArtifactImage(artifactId, {
      image_base64: variant.image_base64,
      // A slide image IS an inline image as far as the renderer is concerned.
      placement: isSlide ? 'inline' : placement,
      caption, alt_text: altText, prompt,
      brand_asset_ids: attachedIds,
      revision_of_id: refining ? target.imageId : undefined,
      thought_signature: variant.thought_signature || target?.thoughtSignature,
    }),
    onSuccess: (img) => {
      if (!inDocument) {
        toast.success(isCover ? 'Cover saved as a draft, pending approval'
          : isSlide ? 'Placed on the slide as a draft, pending approval'
            : 'Standalone image saved as a draft, pending approval');
      } else {
        const attrs = {
          src: img.url,
          'data-training-image-id': String(img.id),
          'data-status': img.status,
          // Display only. The docx renderer reads the authoritative caption from
          // the image record by id; this just makes the editor show what the
          // download will contain.
          'data-caption': caption || null,
          alt: altText || '',
        };
        if (refining) {
          // Point the existing node at the new revision. The document save that
          // follows is what commits the swap.
          editor?.chain().focus().updateAttributes('image', attrs).run();
          toast.success('Replaced. Save the artifact to keep it.');
        } else {
          editor?.chain().focus().setImage(attrs).run();
          toast.success('Inserted as a draft, pending approval');
        }
      }
      // The saved record travels, so a slide caller can anchor it.
      onInserted?.(img);
      onClose();
    },
    onError: (e) => toast.error(e?.message || 'Save failed'),
  });

  const addRefs = async (files) => {
    try {
      const parsed = await Promise.all(Array.from(files).map(fileToRef));
      setRefs((cur) => [...cur, ...parsed]);
    } catch {
      toast.error('Could not read one of the reference files');
    }
  };

  const kindLabel = isCover ? 'a cover image'
    : isSlide ? 'an image for this slide'
      : placement === 'standalone' ? 'a standalone image' : 'an image';
  const heading = refining
    ? 'Refine with JPRO Intelligence'
    : `Generate ${kindLabel} with JPRO Intelligence`;

  return (
    <div className="mt-3 rounded-lg border border-[#2b3a4d] bg-[#0f1620] p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide text-[#7d8aa0]">{heading}</div>
        <button onClick={onClose} className="text-[#8a95a8] hover:text-[#e6edf6]" title="Close">
          <X size={14} />
        </button>
      </div>

      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={2}
        placeholder={refining
          ? 'What should change? e.g. make the background white, keep everything else the same'
          : 'Describe the image, e.g. a clean flat-style diagram of the income calculation flow'}
        className="w-full text-xs bg-transparent border border-[#2b3a4d] rounded px-2 py-1 text-[#c7d0e0]"
      />

      <div className="flex flex-wrap items-center gap-1.5">
        {assets.map((a) => {
          const on = isAttached(a);
          return (
            <button
              key={a.id}
              onClick={() => toggle(a)}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                on ? 'bg-blue-600/20 border-blue-500 text-[#e6edf6]'
                   : 'border-[#2b3a4d] text-[#8a95a8]'}`}
              title={a.notes || a.label}
            >
              {a.url && <img src={a.url} alt="" className="h-3 w-3 object-contain rounded-sm" />}
              {a.label}
              {a.usage_mode === 'overlay' && (
                <span className="text-[9px] text-[#5f6b7e]">stamped</span>
              )}
              {on ? <X size={10} /> : <span className="text-[10px]">+</span>}
            </button>
          );
        })}
        <label className="text-[11px] px-2 py-0.5 rounded-full border border-[#2b3a4d] text-[#8a95a8] cursor-pointer">
          Add a reference file
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => { if (e.target.files.length) addRefs(e.target.files); e.target.value = ''; }}
          />
        </label>
        {refs.length > 0 && (
          <span className="text-[10px] text-[#5f6b7e]">
            {refs.length} file(s)
            <button onClick={() => setRefs([])} className="ml-1 text-[#f7a23b]">clear</button>
          </span>
        )}
      </div>

      {overCap && (
        <div className="text-[10px] text-[#f7a23b]">
          That is more than {MAX_REFERENCES} references. The extras will not be used.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="caption"
          className="text-[11px] bg-transparent border border-[#2b3a4d] rounded px-2 py-1 flex-1 min-w-[120px] text-[#c7d0e0]"
        />
        {variants.length > 0 && (
          <span className="text-[10px] text-[#5f6b7e]">
            or use "Suggest" under an image below
          </span>
        )}
        <input
          value={altText}
          onChange={(e) => setAltText(e.target.value)}
          placeholder="alt text"
          className="text-[11px] bg-transparent border border-[#2b3a4d] rounded px-2 py-1 flex-1 min-w-[120px] text-[#c7d0e0]"
        />
        <select
          value={variantCount}
          onChange={(e) => setVariantCount(Number(e.target.value))}
          className="text-[11px] bg-[#0f1620] border border-[#3a4a5f] rounded px-2 py-1 text-[#e6ecf5] [&>option]:bg-[#0f1620]"
        >
          {[1, 2, 3, 4].map((n) => (
            <option key={n} value={n}>{n} option{n > 1 ? 's' : ''}</option>
          ))}
        </select>
        <button
          onClick={() => gen.mutate()}
          disabled={!prompt.trim() || gen.isPending || save.isPending}
          className="inline-flex items-center gap-1 text-[11px] px-3 py-1 rounded-lg bg-[#1f6feb] text-white disabled:opacity-40"
        >
          {gen.isPending ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
          {gen.isPending ? 'Generating' : 'Generate'}
        </button>
      </div>

      {(gen.isPending || save.isPending) && (
        <div className="rounded-lg border border-[#1f6feb]/40 bg-[#1f6feb]/5 p-2">
          <RotatingStatusIndicator inline statements={IMAGE_GEN_STATEMENTS} />
          <div className="text-[10px] text-[#5f6b7e] mt-1">
            {save.isPending
              ? 'Saving the image'
              : 'This usually takes 20 to 40 seconds. You can keep editing while it runs.'}
          </div>
        </div>
      )}

      {variants.length > 0 && !gen.isPending && (
        <div className="grid grid-cols-2 gap-2 pt-1">
          {variants.map((v, i) => (
            <div key={i} className="border border-[#2b3a4d] rounded p-1">
              <img
                src={`data:image/png;base64,${v.image_base64}`}
                alt={`Option ${i + 1}`}
                className="w-full rounded"
              />
              <button
                onClick={() => save.mutate(v)}
                disabled={save.isPending}
                className="mt-1 w-full text-[11px] px-2 py-1 rounded bg-[#1f6feb] text-white disabled:opacity-40"
              >
                {refining ? 'Replace with this' : 'Use this'}
              </button>
              {/* Per variant on purpose: a single button captioned variants[0] while the
                  user could save any of them, so the caption could describe a different
                  picture than the one saved. */}
              <button
                onClick={() => suggestCaption.mutate(v)}
                disabled={suggestCaption.isPending}
                title="Fill in the caption and alt text from this image"
                className="mt-1 w-full inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1 rounded border border-[#2b3a4d] text-[#5ea9ff] disabled:opacity-40"
              >
                <Sparkles size={11} />
                {suggestCaption.isPending ? 'Reading…' : 'Suggest caption'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
